import { Box, Typography, Button, useTheme, keyframes } from "@mui/material";
import { ErrorOutline, Refresh } from "@mui/icons-material";
import { styled } from "@mui/system";

// Animation for floating effect
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

// Animation for pulse effect
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

const AnimatedBox = styled(Box)({
  animation: `${float} 3s ease-in-out infinite`,
});

const AnimatedButton = styled(Button)({
  animation: `${pulse} 2s ease-in-out infinite`,
});

const ErrorPage = ({ error, onRetry }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "70vh",
        textAlign: "center",
        p: 3,
        background: "linear-gradient(135deg, #f8f8f8 0%, #ffffff 100%)",
        borderRadius: 2,
        position: "relative",
        overflow: "hidden",
        "&:before": {
          content: '""',
          position: "absolute",
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          background: `radial-gradient(circle, ${theme.palette.primary.light} 0%, transparent 70%)`,
          opacity: 0.1,
        },
        "&:after": {
          content: '""',
          position: "absolute",
          bottom: -30,
          left: -30,
          width: 150,
          height: 150,
          background: `radial-gradient(circle, ${theme.palette.secondary.light} 0%, transparent 70%)`,
          opacity: 0.1,
        },
      }}
    >
      <AnimatedBox>
        <ErrorOutline
          sx={{
            fontSize: 80,
            color: theme.palette.error.main,
            mb: 2,
          }}
        />
      </AnimatedBox>

      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          mb: 2,
        }}
      >
        We Apologize for the Inconvenience
      </Typography>

      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 400,
          color: theme.palette.text.secondary,
          mb: 3,
          maxWidth: "600px",
          lineHeight: 1.6,
        }}
      >
        An unexpected error occurred while processing your request
      </Typography>

      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          p: 3,
          borderRadius: 1,
          boxShadow: theme.shadows[1],
          maxWidth: "600px",
          width: "100%",
          mb: 4,
          borderLeft: `3px solid ${theme.palette.error.main}`,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.primary,
            mb: 1,
          }}
        >
          {error?.message || "Our team has been notified about this issue"}
        </Typography>
        {error?.code && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.disabled,
              fontFamily: "monospace",
            }}
          >
            Reference: {error.code}
          </Typography>
        )}
      </Box>

      {onRetry && (
        <AnimatedButton
          variant="contained"
          color="primary"
          onClick={onRetry}
          startIcon={<Refresh />}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 1,
            fontWeight: 500,
            textTransform: "none",
            fontSize: "0.9375rem",
          }}
        >
          Retry Now
        </AnimatedButton>
      )}

      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          mt: 4,
          maxWidth: "600px",
        }}
      >
        For persistent issues, please contact our support team at
        support@halalworld.com
      </Typography>
    </Box>
  );
};

export default ErrorPage;
